import React from 'react';
import { 
  Target, Eye, Heart, Award, ShieldCheck, Users, 
  Briefcase, GraduationCap, Laptop, ChevronRight, 
  CheckCircle2, Building2, TrendingUp, Landmark
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();

  const values = [
    { title: 'Transparency', icon: <Eye size={24} className="text-gold-primary" /> },
    { title: 'Integrity', icon: <ShieldCheck size={24} className="text-gold-primary" /> },
    { title: 'Customer First', icon: <Heart size={24} className="text-gold-primary" /> },
    { title: 'Excellence', icon: <Award size={24} className="text-gold-primary" /> },
  ];

  const profileParagraphs = t('about.profile.paragraphs', { returnObjects: true });
  const expertisePoints = t('about.profile.expertisePoints', { returnObjects: true });

  return (
    <div className="pt-10">
      {/* Header Section */}
      <section className="bg-dark-green py-20 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gold-primary/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('about.header.title')}</h1>
          <p className="text-xl text-gold-light max-w-3xl mx-auto leading-relaxed italic">
            "{t('about.header.subtitle')}"
          </p>
        </div>
      </section>

      {/* Profile Narrative Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            {/* Sidebar with Image */}
            <div className="lg:w-1/3 lg:sticky top-24">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gold-primary/20 rounded-2xl blur-xl transition-all"></div>
                <div className="relative bg-cream-off rounded-2xl overflow-hidden shadow-2xl border-2 border-gold-primary/30">
                  <img 
                    src="/assets/images/file.jpeg" 
                    alt={t('about.profile.name')} 
                    className="w-full aspect-[4/5] object-cover"
                  />
                  <div className="p-6 bg-dark-green text-white text-center">
                    <h2 className="text-2xl font-bold">{t('about.profile.name')}</h2>
                    <p className="text-gold-primary font-medium text-sm mt-1 leading-tight">
                      {t('about.profile.role')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Expertise Stats */}
              <div className="mt-12 grid grid-cols-2 gap-4">
                <div className="bg-cream-off p-4 rounded-xl text-center border border-gold-primary/20">
                  <p className="text-3xl font-bold text-dark-green">20+</p>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-1">Years Exp.</p>
                </div>
                <div className="bg-cream-off p-4 rounded-xl text-center border border-gold-primary/20">
                  <p className="text-3xl font-bold text-dark-green">50+</p>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-1">Team Size</p>
                </div>
              </div>
            </div>
            
            {/* Narrative Content */}
            <div className="lg:w-2/3">
              <div className="prose prose-lg max-w-none">
                <h3 className="text-3xl font-bold text-dark-green mb-8 flex items-center gap-3">
                  <Briefcase className="text-gold-primary" /> Professional Profile
                </h3>
                
                <div className="space-y-6 text-gray-700 leading-relaxed text-lg text-justify">
                  {profileParagraphs.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </div>

              {/* Core Areas of Expertise Grid */}
              <div className="mt-16">
                <h3 className="text-2xl font-bold text-dark-green mb-8 flex items-center gap-3">
                  <TrendingUp className="text-gold-primary" /> {t('about.profile.expertiseTitle')}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {expertisePoints.map((point, i) => (
                    <div key={i} className="flex items-center gap-3 bg-cream-off p-4 rounded-xl border border-gold-light/20 hover:border-gold-primary/40 transition-colors">
                      <CheckCircle2 size={18} className="text-gold-primary flex-shrink-0" />
                      <span className="text-dark-green font-semibold text-sm">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-cream-off">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-dark-green mb-4">{t('about.vision.mission')} & {t('about.vision.vision')}</h2>
          <div className="w-16 h-1 bg-gold-primary mx-auto mb-16"></div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-10 rounded-3xl relative group overflow-hidden shadow-sm">
              <div className="absolute top-0 left-0 w-2 h-full bg-dark-green"></div>
              <div className="bg-dark-green w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Target className="text-white" size={32} />
              </div>
              <h4 className="text-2xl font-bold text-dark-green mb-4">{t('about.vision.mission')}</h4>
              <p className="text-gray-600 leading-relaxed">
                {t('about.vision.missionDesc')}
              </p>
            </div>
            
            <div className="bg-white p-10 rounded-3xl relative group overflow-hidden shadow-sm">
              <div className="absolute top-0 left-0 w-2 h-full bg-gold-primary"></div>
              <div className="bg-gold-primary w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Eye className="text-white" size={32} />
              </div>
              <h4 className="text-2xl font-bold text-dark-green mb-4">{t('about.vision.vision')}</h4>
              <p className="text-gray-600 leading-relaxed">
                {t('about.vision.visionDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-dark-green">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-white mb-16">{t('about.vision.values')}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <div key={i} className="bg-white/5 p-8 rounded-2xl text-center border border-white/10 hover:border-gold-primary/50 transition-all group">
                <div className="inline-block p-4 bg-white/10 rounded-full mb-6 group-hover:bg-gold-primary/20 transition-colors">
                  {v.icon}
                </div>
                <h4 className="text-xl font-bold text-white">{t(`${v.title.replace(' ', '')}`)}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;

